import React from "react";
import { useLocation } from "wouter";
import { formatCurrency } from "../../../utils/numbers";
import { ChartCardHeader, ChartTitle } from "../styles";
import { Transaction } from "../../../services/transactions/types";
import { colorForCategory } from "../../../utils";
import { TransactionTone } from "../types";
import {
  formatTransactionDate,
  getTransactionTitle,
  getTransactionTone,
} from "../utils";
import Button, { ButtonTypes } from "../../../components/Button";
import { CardFooterLink } from "../../../components/CardFooterLink";
import {
  CategoryBadge,
  TransactionAmount,
  TransactionCategory,
  TransactionDate,
  TransactionInfo,
  TransactionItem,
  TransactionList,
  TransactionMeta,
  TransactionsEmpty,
  TransactionTitle,
} from "./styles";

interface RecentTransactionsProps {
  transactions: Transaction[];
  onCreateClick: () => void;
  onTransactionClick: (tx: Transaction) => void;
}

const AMOUNT_SIGN: Record<TransactionTone, string> = {
  positive: "+",
  negative: "-",
  neutral: "",
};

export const RecentTransactions: React.FC<RecentTransactionsProps> = React.memo(
  ({ transactions, onCreateClick, onTransactionClick }) => {
    const [, setLocation] = useLocation();
    const isEmpty = transactions.length === 0;

    const listBody = isEmpty ? (
      <TransactionsEmpty>
        No transactions yet — add your first one to see it here.
      </TransactionsEmpty>
    ) : (
      <TransactionList role="list" aria-label="Recent transactions">
        {transactions.map((tx) => {
          const tone = getTransactionTone(tx);
          const formatted = formatCurrency(tx.amount);
          const display = `${AMOUNT_SIGN[tone]}${formatted}`;
          let amountNoun: string = tx.transactionType;
          if (tone === "positive") {
            amountNoun = "Income";
          } else if (tone === "negative") {
            amountNoun = "Expense";
          }
          const categoryLabel = tx.categoryName || tx.transactionType;
          const badgeCode = categoryLabel.slice(0, 2).toUpperCase();
          const badgeColor = colorForCategory(categoryLabel);
          const title = getTransactionTitle(tx);
          return (
            <TransactionItem
              key={tx.id}
              role="listitem"
              onClick={() => onTransactionClick(tx)}
            >
              <CategoryBadge $color={badgeColor} aria-hidden="true">
                {badgeCode}
              </CategoryBadge>
              <TransactionInfo>
                <TransactionTitle title={title}>{title}</TransactionTitle>
                <TransactionCategory>{categoryLabel}</TransactionCategory>
              </TransactionInfo>
              <TransactionMeta>
                <TransactionAmount
                  $tone={tone}
                  aria-label={`${amountNoun}: ${formatted}`}
                >
                  {display}
                </TransactionAmount>
                <TransactionDate>
                  {formatTransactionDate(tx.transactionDate)}
                </TransactionDate>
              </TransactionMeta>
            </TransactionItem>
          );
        })}
      </TransactionList>
    );

    return (
      <>
        <ChartCardHeader>
          <ChartTitle>Recent Transactions</ChartTitle>
          <Button
            type={ButtonTypes.primary}
            size="small"
            onClick={onCreateClick}
            aria-label="Add transaction"
          >
            + Add
          </Button>
        </ChartCardHeader>
        {listBody}
        <CardFooterLink onClick={() => setLocation("/transactions")}>
          View all transactions →
        </CardFooterLink>
      </>
    );
  },
);

RecentTransactions.displayName = "RecentTransactions";
